import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { formatDurationMinutes } from '../../../common/workflowTiming.js';

function formatTaskOffset(task) {
  const minutes = Math.max(0, Number(task?.relOffsetMinutes) || 0);
  const referenceLabel = task?.offsetReferenceKind === 'workflow_start'
    ? 'ab Workflow-Start'
    : 'nach vorherigem Task';

  if (minutes < 1) {
    return `ohne Versatz ${referenceLabel}`;
  }

  return `+${formatDurationMinutes(minutes)} ${referenceLabel}`;
}

function formatTaskBoundary(timeValue, isPreviousDay, isNextDay) {
  const timeLabel = String(timeValue ?? '').trim() || '-';

  if (isPreviousDay) {
    return `Vortag ${timeLabel}`;
  }

  if (isNextDay) {
    return `Folgetag ${timeLabel}`;
  }

  return timeLabel;
}

function parseDateParts(dateStr) {
  const raw = String(dateStr ?? '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return null;
  }

  const [year, month, day] = raw.split('-').map(Number);
  return { year, month, day };
}

function parseTimeParts(timeStr) {
  const raw = String(timeStr ?? '').trim();
  if (!/^\d{2}:\d{2}$/.test(raw)) {
    return null;
  }

  const [hours, minutes] = raw.split(':').map(Number);
  return { hours, minutes };
}

function buildDateTime(dateStr, timeStr, dayOffset = 0) {
  const dateParts = parseDateParts(dateStr);
  const timeParts = parseTimeParts(timeStr);
  if (!dateParts || !timeParts) {
    return null;
  }

  return new Date(
    dateParts.year,
    dateParts.month - 1,
    dateParts.day + dayOffset,
    timeParts.hours,
    timeParts.minutes,
    0,
    0
  );
}

function buildTaskWindow(task) {
  const baseDate = String(task?.occurrenceDate ?? '').trim();
  const startValue = task?.actualStart;
  const endValue = task?.actualEnd;

  const startAt = buildDateTime(
    baseDate,
    startValue,
    task?.startsPreviousDay ? -1 : 0
  );
  let endAt = buildDateTime(
    baseDate,
    endValue,
    task?.endsNextDay ? 1 : 0
  );

  if (!startAt || !endAt) {
    return null;
  }

  if (endAt <= startAt) {
    endAt = buildDateTime(baseDate, endValue, 1);
  }

  if (!endAt) {
    return null;
  }

  return { startAt, endAt };
}

function formatRemainingCountdown(endAt, nowTs) {
  if (!(endAt instanceof Date) || Number.isNaN(endAt.getTime())) {
    return '';
  }

  const totalSeconds = Math.max(0, Math.floor((endAt.getTime() - nowTs) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `noch ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  return `noch ${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function parseSourceTaskId(rawTaskId) {
  const raw = String(rawTaskId ?? '').trim();
  if (!raw) {
    return null;
  }

  const match = raw.match(/^(\d+):/);
  if (match) {
    return Number(match[1]);
  }

  return /^\d+$/.test(raw) ? Number(raw) : null;
}

function dispatchTaskEdit(taskId) {
  if (!Number.isInteger(taskId) || taskId < 1) {
    return;
  }

  document.dispatchEvent(new CustomEvent('ui:entity-action', {
    detail: {
      entity: 'tasks',
      action: 'edit',
      rowId: String(taskId),
      selectedIds: [String(taskId)],
      source: 'timeline-task-info-modal',
    },
  }));
}

function InfoField({ label, value }) {
  return (
    <div className="timeline-task-info-field">
      <div className="timeline-task-info-field-label">{label}</div>
      <div className="timeline-task-info-field-value">{value}</div>
    </div>
  );
}

export default function TimelineTaskInfoModal({ task, onClose }) {
  const modalRef = useRef(null);
  const [nowTs, setNowTs] = useState(() => Date.now());
  const isOpen = Boolean(task);

  const sourceTaskId = useMemo(() => parseSourceTaskId(task?.taskId), [task]);
  const canEditTask = Number.isInteger(sourceTaskId) && sourceTaskId > 0;
  const taskWindow = useMemo(() => buildTaskWindow(task), [task]);
  const isCurrentlyRunning = useMemo(() => {
    if (!taskWindow) {
      return false;
    }

    return nowTs >= taskWindow.startAt.getTime() && nowTs < taskWindow.endAt.getTime();
  }, [nowTs, taskWindow]);
  const remainingCountdown = useMemo(() => {
    if (!isCurrentlyRunning || !taskWindow) {
      return '';
    }

    return formatRemainingCountdown(taskWindow.endAt, nowTs);
  }, [isCurrentlyRunning, nowTs, taskWindow]);

  useEffect(() => {
    if (!task) {
      return undefined;
    }

    setNowTs(Date.now());
    const timerId = window.setInterval(() => {
      setNowTs(Date.now());
    }, 1000);

    return () => window.clearInterval(timerId);
  }, [task]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const modalEl = modalRef.current;
    const ModalCtor = window.bootstrap?.Modal;
    if (!modalEl || !ModalCtor) {
      return undefined;
    }

    const modal =
      typeof ModalCtor.getOrCreateInstance === 'function'
          ? ModalCtor.getOrCreateInstance(modalEl, {
            backdrop: 'static',
            keyboard: true,
            focus: true,
          })
        : new ModalCtor(modalEl, {
            backdrop: 'static',
            keyboard: true,
            focus: true,
          });

    function handleHidden() {
      onClose?.();
    }

    modalEl.addEventListener('hidden.bs.modal', handleHidden);
    modal.show();

    requestAnimationFrame(() => {
      const backdrops = Array.from(document.querySelectorAll('.modal-backdrop'));
      const latestBackdrop = backdrops[backdrops.length - 1];
      latestBackdrop?.classList.add('timeline-task-info-backdrop');
    });

    return () => {
      modalEl.removeEventListener('hidden.bs.modal', handleHidden);
      modal.dispose?.();
    };
  }, [isOpen, onClose]);

  if (!task || typeof document === 'undefined') {
    return null;
  }

  function requestClose() {
    const modalEl = modalRef.current;
    const ModalCtor = window.bootstrap?.Modal;
    if (!modalEl || !ModalCtor) {
      onClose?.();
      return;
    }

    const modal =
      typeof ModalCtor.getInstance === 'function'
        ? ModalCtor.getInstance(modalEl)
        : null;

    if (modal) {
      modal.hide();
      return;
    }

    onClose?.();
  }

  function openTaskEdit() {
    if (!canEditTask) {
      return;
    }

    dispatchTaskEdit(sourceTaskId);
  }

  const modalNode = (
    <div
      ref={modalRef}
      className="modal fade timeline-task-info-modal"
      tabIndex="-1"
      aria-labelledby="timelineTaskInfoModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered timeline-task-info-dialog">
        <div className="modal-content bg-dark text-light border-secondary timeline-task-info-content">
          <div className="modal-header border-secondary timeline-task-info-header">
            <div className="timeline-task-info-header-copy">
              <h5
                id="timelineTaskInfoModalLabel"
                className="modal-title mb-0 timeline-task-info-title"
              >
                {task.taskName ?? 'Task'}
              </h5>
            </div>

            <div className="timeline-task-info-header-actions">
              {canEditTask ? (
                <button
                  type="button"
                  className="timeline-task-info-action-btn"
                  onClick={openTaskEdit}
                  title="Task bearbeiten"
                  aria-label="Task bearbeiten"
                >
                  <i className="bi bi-pencil" aria-hidden="true" />
                </button>
              ) : null}

              <button
                type="button"
                className="timeline-task-info-action-btn"
                aria-label="Schließen"
                onClick={requestClose}
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="modal-body d-grid gap-3 timeline-task-info-body">
            <div className="timeline-task-info-topline">
              {task.workflowName ? (
                <div className="timeline-task-info-workflow">
                  <span className="timeline-task-info-workflow-label">Workflow</span>
                  <span className="timeline-task-info-workflow-value">{task.workflowName}</span>
                </div>
              ) : <span />}

              <div className="timeline-task-info-meta-badges">
                {remainingCountdown ? (
                  <span className="timeline-task-info-running-badge">
                    {remainingCountdown}
                  </span>
                ) : null}

                {Number(task?.otherActiveTaskCount ?? 0) > 0 ? (
                  <div className="timeline-task-info-note" role="note">
                    Zusätzlich {task.otherActiveTaskCount} weitere aktiv
                  </div>
                ) : null}
              </div>
            </div>

            <div className="timeline-task-info-grid">
              <InfoField label="Versatz" value={formatTaskOffset(task)} />
              <InfoField
                label="Dauer"
                value={formatDurationMinutes(Math.max(0, Number(task?.durationMinutes) || 0))}
              />
              <InfoField
                label="Start"
                value={formatTaskBoundary(
                  task?.actualStart,
                  Boolean(task?.startsPreviousDay),
                  false
                )}
              />
              <InfoField
                label="Ende"
                value={formatTaskBoundary(
                  task?.actualEnd,
                  false,
                  Boolean(task?.endsNextDay)
                )}
              />
            </div>

            <div className="timeline-task-info-description-block">
              <div className="timeline-task-info-field-label">Beschreibung</div>
              {String(task?.description ?? '').trim() ? (
                <div className="timeline-task-info-description">
                  {String(task.description)}
                </div>
              ) : (
                <div className="timeline-task-info-description-empty">
                  Keine Beschreibung hinterlegt.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalNode, document.body);
}
